import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import { useCallback, useState, useRef, useEffect, Fragment } from 'react';
import MojitoServices from 'mojito/services';

const classUtils = MojitoCore.Base.classUtils;
const {
    ContextExtender: AnalyticsContextExtender,
    Context: AnalyticsContext,
    ContextBuilder: AnalyticsContextBuilder,
} = MojitoCore.Presentation.AnalyticsContext;
const { useComponentVisibilityAfterScrollRestoration } = MojitoPresentation.Hooks;
const { TOP_SPORTS_PRIMARY_TAB_ID, TOP_SPORTS_SECONDARY_TAB_ID } =
    MojitoModules.CmsCouponMenuLinks.constants;
const { PrebuiltPromotionCards } = MojitoModules.Promotions;
const { usePromotionsLoadDone } = MojitoModules.Promotions.hooks;
const { FlexPane, PostponedRenderer } = MojitoPresentation.Components;
const EventCardsCarousel = MojitoModules.EventCardsCarousel.module;
const SportsFilterContainer = MojitoModules.SportsFilterContainer.module;
const TopSports = MojitoModules.TopSports.module;
const AnalyticsTypes = MojitoServices.Analytics.types;
const { HOME_PAGE } = AnalyticsTypes.SOURCE;

const extendAnalyticsContext = (context, value) => ({ ...context, ...value });
const selectionSourceContext = new AnalyticsContextBuilder().withAnalyticsPath(HOME_PAGE).build();
const analyticsContext = {
    marketChangeAnalyticsType: 'homePageEventListMarketSelectorValueChanged',
};

const applicablePage = 'homepage';

const withSportsFilter = (config, showFilter, filterConfigKey, renderContent) => {
    if (!showFilter) {
        return renderContent(undefined, undefined);
    }

    return (
        <SportsFilterContainer config={config[filterConfigKey]}>
            {renderContent}
        </SportsFilterContainer>
    );
};

export default function ProView(props) {
    const { mojitoTools, class: className } = props;
    const { config } = mojitoTools;

    return (
        <FlexPane config={config.container} class={classUtils.classNames('ta-ProView', className)}>
            <LayoutBuilder {...props} layoutParts={layoutParts} />
        </FlexPane>
    );
}

const layoutParts = {
    EventCardsCarousel: props => <EventCardsCarouselPart {...props} />,
    PropCardsCarousel: props => <PropCardsCarouselPart {...props} />,
    PrebuiltBetBuilderPromotionCards: props => <PrebuiltBetBuilderPromotionCardsPart {...props} />,
    PrebuiltAccaPromotionCards: props => <PrebuiltAccaPromotionCardsPart {...props} />,
    TopSportsPrimary: props => <TopSportsPrimaryPart {...props} />,
    TopSportsSecondary: props => <TopSportsSecondaryPart {...props} />,
};

const LayoutBuilder = props => {
    const {
        layoutParts,
        mojitoTools: { config },
    } = props;
    const promotionsLoaded = usePromotionsLoadDone();
    const prevPromotionsLoadedRef = useRef(promotionsLoaded);
    const persistOnRerender = !prevPromotionsLoadedRef.current && promotionsLoaded;

    useEffect(() => {
        prevPromotionsLoadedRef.current = promotionsLoaded;
    }, [promotionsLoaded]);

    let postponedPriority = 0;

    return Object.entries(config.layoutParts)
        .sort(([, paramsA], [, paramsB]) => paramsA.position - paramsB.position)
        .filter(([name, params]) => layoutParts[name] && params.show)
        .map(([name, params]) => {
            const Component = layoutParts[name];
            let content = <Component {...props} />;

            if (params.postponed) {
                const priority = postponedPriority;
                postponedPriority += 1;
                content = (
                    <PostponedRenderer
                        name={name}
                        priority={priority}
                        persistOnRerender={persistOnRerender}
                    >
                        {content}
                    </PostponedRenderer>
                );
            }

            if (params.awaitPromotions) {
                content = (
                    <AwaitPromotions config={config} promotionsLoaded={promotionsLoaded}>
                        {content}
                    </AwaitPromotions>
                );
            }

            return <Fragment key={name}>{content}</Fragment>;
        });
};

const EventCardsCarouselPart = ({ mojitoTools: { config, stringResolver } }) => (
    <EventCardsCarousel
        config={config.eventCardsCarousel}
        pageId={applicablePage}
        headerText={stringResolver.resolveString('$PRO_VIEW.EVENT_CARDS_CAROUSEL_TITLE')}
    />
);

const PropCardsCarouselPart = ({ mojitoTools: { config } }) => {
    const renderCarousel = (selectedSportId, onSportsChange) => (
        <EventCardsCarousel
            config={config.propCardsCarousel}
            pageId={applicablePage}
            selectedSportId={selectedSportId}
            onSportsChange={onSportsChange}
            class="ta-ProView-propCardsCarousel"
        />
    );

    return withSportsFilter(
        config,
        config.showPropCardsCarouselSportsFilter,
        'propCardsCarouselSportsFilter',
        renderCarousel
    );
};

const createPrebuiltPromotionCardsPart = ({
    cardsConfigKey,
    showSportsFilterKey,
    sportsFilterConfigKey,
    className,
}) => {
    const Part = ({ mojitoTools: { config } }) => {
        const cardsConfig = config[cardsConfigKey];
        const renderCards = (selectedSportId, onSportsChange) => (
            <PrebuiltPromotionCards
                applicablePage={applicablePage}
                config={cardsConfig}
                selectedSportId={selectedSportId}
                onSportsChange={onSportsChange}
                class={className}
            />
        );

        return (
            <AnalyticsContext.Provider value={selectionSourceContext}>
                {withSportsFilter(
                    config,
                    config[showSportsFilterKey],
                    sportsFilterConfigKey,
                    renderCards
                )}
            </AnalyticsContext.Provider>
        );
    };

    return Part;
};

const PrebuiltBetBuilderPromotionCardsPart = createPrebuiltPromotionCardsPart({
    cardsConfigKey: 'prebuiltBetBuilderPromotionCards',
    showSportsFilterKey: 'showPrebuiltBetBuilderPromotionCardsSportsFilter',
    sportsFilterConfigKey: 'prebuiltBetBuilderPromotionCardsSportsFilter',
    className: 'ta-ProView-prebuiltBetBuilderPromotionCards',
});

const PrebuiltAccaPromotionCardsPart = createPrebuiltPromotionCardsPart({
    cardsConfigKey: 'prebuiltAccaPromotionCards',
    showSportsFilterKey: 'showPrebuiltAccaPromotionCardsSportsFilter',
    sportsFilterConfigKey: 'prebuiltAccaPromotionCardsSportsFilter',
    className: 'ta-ProView-prebuiltAccaPromotionCards',
});

const TopSportsPrimaryPart = props => {
    const { mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const { primary: primaryCoupon } = config.topSportCoupons;

    return (
        <AnalyticsContextExtender
            value={analyticsContext}
            extendingStrategy={extendAnalyticsContext}
        >
            <TopSportsContent mojitoTools={mojitoTools} sectionId={TOP_SPORTS_PRIMARY_TAB_ID}>
                <TopSports
                    config={primaryCoupon.coupon}
                    navigationTitle={stringResolver.resolveString(
                        '$PRO_VIEW.PRIMARY_TOP_SPORTS_TITLE'
                    )}
                />
            </TopSportsContent>
        </AnalyticsContextExtender>
    );
};

const TopSportsSecondaryPart = props => {
    const { mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const { secondary: secondaryCoupon } = config.topSportCoupons;

    return (
        <AnalyticsContextExtender
            value={analyticsContext}
            extendingStrategy={extendAnalyticsContext}
        >
            <TopSportsContent mojitoTools={mojitoTools} sectionId={TOP_SPORTS_SECONDARY_TAB_ID}>
                <TopSports
                    config={secondaryCoupon.coupon}
                    navigationTitle={stringResolver.resolveString(
                        '$PRO_VIEW.SECONDARY_TOP_SPORTS_TITLE'
                    )}
                />
            </TopSportsContent>
        </AnalyticsContextExtender>
    );
};

const TopSportsContent = props => {
    const { children, sectionId, mojitoTools } = props;
    const { config, style } = mojitoTools;
    const [elementRef, visible] = useTopSportsVisibility(config);
    const topSportsContainerConfig = style.topSportsContainer ?? config.topSportsContainer;

    return (
        <FlexPane
            id={sectionId}
            elementRef={elementRef}
            class="ta-TopSportsSection"
            config={topSportsContainerConfig}
        >
            {visible ? children : null}
        </FlexPane>
    );
};

const isElementInViewport = el => {
    if (!el) {
        return false;
    }
    const rect = el.getBoundingClientRect();
    const isInRange = (num, start, end) => {
        const leftInRange = num >= start;
        const rightInRange = num < end;
        return leftInRange && rightInRange;
    };
    return (
        (isInRange(rect.top, 0, window.innerHeight) ||
            isInRange(rect.bottom, 0, window.innerHeight)) &&
        (isInRange(rect.left, 0, window.innerWidth) || isInRange(rect.right, 0, window.innerWidth))
    );
};

const useTopSportsVisibility = config => {
    const [visible, setVisible] = useState(!config.topSportCoupons.optimizeRendering);
    const onShowUp = useCallback(() => setVisible(true), []);
    const visibilityRef = useComponentVisibilityAfterScrollRestoration(onShowUp);
    const elementRef = useCallback(
        element => {
            if (isElementInViewport(element)) {
                setVisible(true);
            }
            visibilityRef(element);
        },
        [visibilityRef]
    );
    return [elementRef, visible];
};

const AwaitPromotions = ({ config, promotionsLoaded, children }) => {
    const { layoutParts } = config;
    const hasPromotionAwareContent =
        layoutParts.EventCardsCarousel?.show ||
        layoutParts.PropCardsCarousel?.show ||
        layoutParts.PrebuiltBetBuilderPromotionCards?.show ||
        layoutParts.PrebuiltAccaPromotionCards?.show;

    if (!promotionsLoaded && hasPromotionAwareContent) {
        return null;
    }

    return children;
};

ProView.getStyle = (config, mode, merge) => {
    const scrollOffset = config.topSportCoupons.scrollOffset;
    return {
        container: merge(config.container, {
            style: {
                margin: config.margin,
            },
        }),
        topSportsContainer: merge(config.topSportsContainer, {
            style: { scrollMargin: `${scrollOffset} 0 0 0` },
        }),
    };
};
